import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PricingCard = ({ plan, isPopular, onSelectPlan, currency }) => {
  return (
    <div className={`relative bg-card border ${
      isPopular ? 'border-accent shadow-2xl scale-105' : 'border-border'
    } rounded-3xl overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1`}>
      {isPopular && (
        <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-accent to-primary py-2 text-center">
          <span className="text-sm font-semibold text-white">Популярный выбор</span>
        </div>
      )}
      <div className={`p-8 ${isPopular ? 'pt-14' : ''}`}>
        <div className="flex items-center space-x-4 mb-6">
          <div className={`w-14 h-14 rounded-2xl ${
            isPopular ? 'bg-gradient-to-br from-accent to-primary' : 'bg-accent/10'
          } flex items-center justify-center`}>
            <Icon 
              name={plan?.icon} 
              size={28} 
              color={isPopular ? '#FFFFFF' : 'var(--color-accent)'} 
            />
          </div>
          <div>
            <h3 className="text-2xl font-headline font-bold text-foreground">
              {plan?.name}
            </h3>
          </div>
        </div>

        <p className="text-muted-foreground mb-6 min-h-[48px]">
          {plan?.description}
        </p>

        <div className="mb-6">
          {plan?.hasTwoPrices ? (
            <div className="space-y-4">
              <div className="p-4 bg-muted/50 rounded-xl border border-border">
                <div className="flex items-baseline space-x-2 mb-1">
                  <span className="text-3xl font-headline font-bold text-foreground">
                    {currency === 'KZT' 
                      ? `${(plan?.displayPrice || 0)?.toLocaleString('ru-KZ')} ₸`
                      : `$${(plan?.displayPrice || 0)?.toLocaleString('en-US')}`
                    }
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {plan?.billingPeriod} без CRM
                </p>
              </div>
              <div className="p-4 bg-accent/10 rounded-xl border border-accent/20">
                <div className="flex items-baseline space-x-2 mb-1">
                  <span className="text-3xl font-headline font-bold text-accent">
                    {currency === 'KZT' 
                      ? `${(plan?.displayPriceWithCRM || 0)?.toLocaleString('ru-KZ')} ₸`
                      : `$${(plan?.displayPriceWithCRM || 0)?.toLocaleString('en-US')}`
                    }
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mb-1">
                  {plan?.billingPeriodWithCRM || plan?.billingPeriod} с CRM
                </p>
                {plan?.displayMonthlyFeeWithCRM > 0 && (
                  <p className="text-sm text-accent font-medium">
                    + {currency === 'KZT' 
                      ? `${(plan?.displayMonthlyFeeWithCRM || 0)?.toLocaleString('ru-KZ')} ₸/мес`
                      : `$${(plan?.displayMonthlyFeeWithCRM || 0)}/мес`
                    } (абонплата)
                  </p>
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-baseline space-x-2">
                <span className="text-5xl font-headline font-bold text-foreground">
                  {currency === 'KZT' 
                    ? `${(plan?.displayPrice || 0)?.toLocaleString('ru-KZ')} ₸`
                    : `$${(plan?.displayPrice || 0)?.toLocaleString('en-US')}`
                  }
                </span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                {plan?.billingPeriod}
              </p>
              {plan?.displayMonthlyFee > 0 && (
                <p className="text-sm text-accent font-medium mt-1">
                  + {currency === 'KZT' 
                    ? `${(plan?.displayMonthlyFee || 0)?.toLocaleString('ru-KZ')} ₸/мес`
                    : `$${(plan?.displayMonthlyFee || 0)}/мес`
                  } (абонплата)
                </p>
              )}
              {plan?.billingNote && (
                <p className="text-xs text-muted-foreground mt-1">
                  {plan?.billingNote}
                </p>
              )}
            </>
          )}
        </div>

        {plan?.gift && (
          <div className="mb-6 p-4 bg-accent/10 border border-accent/20 rounded-xl">
            <div className="flex items-start space-x-2">
              <Icon name="Gift" size={20} color="var(--color-accent)" className="flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-accent uppercase tracking-wide mb-1">
                  ПОДАРОК
                </p>
                <p className="text-sm text-foreground">
                  {plan?.gift}
                </p>
              </div>
            </div>
          </div>
        )}

        {plan?.gifts && (
          <div className="mb-6 p-4 bg-accent/10 border border-accent/20 rounded-xl">
            <div className="flex items-start space-x-2">
              <Icon name="Gift" size={20} color="var(--color-accent)" className="flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs font-semibold text-accent uppercase tracking-wide mb-2">
                  2 ПОДАРКА
                </p>
                {plan?.gifts?.map((gift, index) => (
                  <p key={index} className="text-sm text-foreground mb-1">
                    {index + 1}. {gift}
                  </p>
                ))}
              </div>
            </div>
          </div>
        )}

        <Button
          variant={isPopular ? 'default' : 'outline'}
          size="lg"
          onClick={onSelectPlan}
          className="w-full mb-6"
        >
          {plan?.ctaText}
        </Button>

        <div className="space-y-3">
          {plan?.features?.map((feature, index) => (
            <div key={index} className="flex items-start space-x-3">
              <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5 ${
                feature?.included 
                  ? 'bg-success/20' :'bg-muted'
              }`}>
                <Icon 
                  name={feature?.included ? 'Check' : 'X'} 
                  size={14} 
                  color={feature?.included ? 'var(--color-success)' : 'var(--color-muted-foreground)'} 
                />
              </div>
              <span className={`text-sm ${
                feature?.included 
                  ? 'text-foreground' 
                  : 'text-muted-foreground line-through'
              }`}>
                {feature?.text}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PricingCard;